import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { customerSalesComparisonService } from '../services/customerSalesComparisonService';
import { CustomerSalesData, CustomerMonthlyData, TransactionDetail } from '../types/SalesReport';
import '../components/sales-reports/SalesReportsPage.css';
import InvoiceDetailDialog from '../components/sales-reports/InvoiceDetailDialog';
import { formatDate, parseDate } from '../utils/date';

const SalesReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to update URL params without causing navigation
  const updateUrlParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerSalesData[]>([]);
  const [monthlyData, setMonthlyData] = useState<CustomerMonthlyData[]>([]);
  const [selectedYears, setSelectedYears] = useState<Set<number>>(() => {
    // Try to load from URL params first
    const yearsParam = searchParams.get('years');
    if (yearsParam) {
      try {
        const arr = yearsParam
          .split(',')
          .map((y) => parseInt(y.trim()))
          .filter((y) => !isNaN(y) && y >= 2023 && y <= new Date().getFullYear());
        if (arr.length > 0 && arr.length <= 3) {
          return new Set(arr);
        }
      } catch {
        // Invalid format, fall through to default
      }
    }
    // Default to current year only
    const currentYear = new Date().getFullYear();
    return new Set([currentYear]);
  });
  const [yearFilterOpen, setYearFilterOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [territoryDropdownOpen, setTerritoryDropdownOpen] = useState(false);
  const [salesPersonDropdownOpen, setSalesPersonDropdownOpen] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [monthTransactions, setMonthTransactions] = useState<Record<string, TransactionDetail[]>>(
    {}
  );
  const [loadingTransactions, setLoadingTransactions] = useState<Set<string>>(new Set());
  const [selectedTransactionYear, setSelectedTransactionYear] = useState<Record<string, number>>(
    {}
  );
  const [selectedRangeTab, setSelectedRangeTab] = useState<Record<string, number>>({});
  const monthTransactionsRef = useRef<Record<string, TransactionDetail[]>>({});
  const loadingTransactionsRef = useRef<Set<string>>(new Set());

  // Keep refs in sync with state
  useEffect(() => {
    monthTransactionsRef.current = monthTransactions;
  }, [monthTransactions]);

  useEffect(() => {
    loadingTransactionsRef.current = loadingTransactions;
  }, [loadingTransactions]);
  const [filterText, setFilterText] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(() => {
    const customersParam = searchParams.get('customers');
    if (customersParam) {
      try {
        return new Set(customersParam.split(',').filter((v) => v.trim() !== ''));
      } catch {
        // Invalid format, return empty set
      }
    }
    return new Set();
  });
  const [customerFilterSearch, setCustomerFilterSearch] = useState('');
  const [allCustomersCache, setAllCustomersCache] = useState<CustomerSalesData[]>([]);
  const [selectedTerritories, setSelectedTerritories] = useState<Set<string>>(() => {
    const territoriesParam = searchParams.get('territories');
    if (territoriesParam) {
      try {
        return new Set(territoriesParam.split(',').filter((v) => v.trim() !== ''));
      } catch {
        // Invalid format, return empty set
      }
    }
    return new Set();
  });
  const [selectedSalesPersonIds, setSelectedSalesPersonIds] = useState<Set<string>>(() => {
    const salesPersonsParam = searchParams.get('salesPersons');
    if (salesPersonsParam) {
      try {
        return new Set(salesPersonsParam.split(',').filter((v) => v.trim() !== ''));
      } catch {
        // Invalid format, return empty set
      }
    }
    return new Set();
  });
  const [territoryFilterSearch, setTerritoryFilterSearch] = useState('');
  const [salesPersonFilterSearch, setSalesPersonFilterSearch] = useState('');
  const [availableTerritories, setAvailableTerritories] = useState<string[]>([]);
  const [availableSalesPersonIds, setAvailableSalesPersonIds] = useState<string[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<TransactionDetail | null>(null);
  const [comparisonMode, setComparisonMode] = useState<'years' | 'customRanges'>(() => {
    const modeParam = searchParams.get('mode');
    return (modeParam === 'customRanges' ? 'customRanges' : 'years') as 'years' | 'customRanges';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [customDateRanges, setCustomDateRanges] = useState<Array<{ from: string; to: string }>>(
    () => {
      const rangesParam = searchParams.get('dateRanges');
      if (rangesParam) {
        try {
          const arr: Array<{ from: string; to: string }> = JSON.parse(
            decodeURIComponent(rangesParam)
          );
          if (Array.isArray(arr) && arr.length > 0 && arr.length <= 3) {
            return arr;
          }
        } catch {
          // Invalid JSON, fall through to default
        }
      }
      return [];
    }
  );

  const loadData = async () => {
    if (comparisonMode === 'years') {
      if (selectedYears.size === 0) {
        setError('Please select at least one year to compare');
        return;
      }
    } else {
      // Custom date ranges mode
      if (customDateRanges.length === 0) {
        setError('Please add at least one custom date range to compare');
        return;
      }
      // Validate date ranges
      const invalidRange = customDateRanges.find((r) => !r.from || !r.to || r.from > r.to);
      if (invalidRange) {
        setError('Please fix invalid date ranges (From date must be before or equal to To date)');
        return;
      }
    }

    setDataLoading(true);
    setError(null);

    try {
      // Convert Sets to arrays for API calls
      // Empty arrays will be treated as "no filter" by the backend (faster than large IN clauses)
      const selectedIdsArray =
        selectedCustomers.size > 0 ? Array.from(selectedCustomers) : undefined;
      const selectedTerritoriesArray =
        selectedTerritories.size > 0 ? Array.from(selectedTerritories) : undefined;
      const selectedSalesPersonIdsArray =
        selectedSalesPersonIds.size > 0 ? Array.from(selectedSalesPersonIds) : undefined;

      if (comparisonMode === 'years') {
        const yearsArray = Array.from(selectedYears).sort((a, b) => a - b);
        const [customers, monthly] = await Promise.all([
          customerSalesComparisonService.getCustomerSalesComparison(
            yearsArray,
            selectedIdsArray,
            selectedTerritoriesArray,
            selectedSalesPersonIdsArray
          ),
          customerSalesComparisonService.getCustomerMonthlySalesComparison(
            yearsArray,
            selectedIdsArray,
            selectedTerritoriesArray,
            selectedSalesPersonIdsArray
          ),
        ]);

        // Handle the API response structure: {success: true, data: {customerData: [...], monthlyData: [...]}}
        const customerArray = customers?.data?.customerData || [];
        const monthlyArray = monthly?.data?.monthlyData || [];

        setCustomerData(customerArray);
        setMonthlyData(monthlyArray);

        // Cache all customers for selection UI (load without filter)
        if (allCustomersCache.length === 0) {
          try {
            const allCustomersResponse =
              await customerSalesComparisonService.getCustomerSalesComparison(yearsArray);
            setAllCustomersCache(allCustomersResponse?.data?.customerData || []);
          } catch (err) {
            console.warn('Failed to load all customers for selection:', err);
          }
        }
      } else {
        // Custom date ranges mode
        const [customers, monthly] = await Promise.all([
          customerSalesComparisonService.getCustomerSalesComparison(
            [], // Empty years array when using custom ranges
            selectedIdsArray,
            selectedTerritoriesArray,
            selectedSalesPersonIdsArray,
            customDateRanges
          ),
          customerSalesComparisonService.getCustomerMonthlySalesComparison(
            [], // Empty years array when using custom ranges
            selectedIdsArray,
            selectedTerritoriesArray,
            selectedSalesPersonIdsArray,
            customDateRanges
          ),
        ]);

        // Handle the API response structure: {success: true, data: {customerData: [...], customDateRanges: [...]}}
        const customerArray = customers?.data?.customerData || [];
        const monthlyArray = monthly?.data?.monthlyData || [];

        setCustomerData(customerArray);
        setMonthlyData(monthlyArray);

        // Cache all customers for selection UI (load without filter)
        if (allCustomersCache.length === 0) {
          try {
            const allCustomersResponse =
              await customerSalesComparisonService.getCustomerSalesComparison(
                [],
                undefined,
                undefined,
                undefined,
                customDateRanges
              );
            setAllCustomersCache(allCustomersResponse?.data?.customerData || []);
          } catch (err) {
            console.warn('Failed to load all customers for selection:', err);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load customer sales data');
      console.error('Error loading customer sales data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (comparisonMode === 'years') {
      if (selectedYears.size === 0) {
        setError('Please select at least one year to export');
        return;
      }
    } else {
      // Custom date ranges mode
      if (customDateRanges.length === 0) {
        setError('Please add at least one custom date range to export');
        return;
      }
      // Validate date ranges
      const invalidRange = customDateRanges.find((r) => !r.from || !r.to || r.from > r.to);
      if (invalidRange) {
        setError('Please fix invalid date ranges (From date must be before or equal to To date)');
        return;
      }
    }

    try {
      console.log('🔍 Starting Excel export...');
      setLoading(true);

      // Convert Sets to arrays for API calls
      // Empty arrays will be treated as "no filter" by the backend (faster than large IN clauses)
      const selectedIdsArray =
        selectedCustomers.size > 0 ? Array.from(selectedCustomers) : undefined;
      const selectedTerritoriesArray =
        selectedTerritories.size > 0 ? Array.from(selectedTerritories) : undefined;
      const selectedSalesPersonIdsArray =
        selectedSalesPersonIds.size > 0 ? Array.from(selectedSalesPersonIds) : undefined;

      if (comparisonMode === 'years') {
        const yearsArray = Array.from(selectedYears).sort((a, b) => a - b);
        const blob = await customerSalesComparisonService.exportToExcel(
          yearsArray,
          selectedIdsArray,
          selectedTerritoriesArray,
          selectedSalesPersonIdsArray
        );
        console.log('🔍 Excel blob received:', blob);
        const yearsStr = yearsArray.join('_');
        const filename = `Customer_Sales_Comparison_${yearsStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
        console.log('🔍 Downloading file:', filename);
        customerSalesComparisonService.downloadExcel(blob, filename);
      } else {
        // Custom date ranges mode
        const blob = await customerSalesComparisonService.exportToExcel(
          [], // Empty years array when using custom ranges
          selectedIdsArray,
          selectedTerritoriesArray,
          selectedSalesPersonIdsArray,
          customDateRanges
        );
        console.log('🔍 Excel blob received:', blob);
        const dateRangesStr = customDateRanges
          .map((r) => `${r.from}_${r.to}`)
          .join('_')
          .replace(/[^a-zA-Z0-9_-]/g, '_');
        const filename = `Customer_Sales_Comparison_CustomRanges_${dateRangesStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
        console.log('🔍 Downloading file:', filename);
        customerSalesComparisonService.downloadExcel(blob, filename);
      }
      console.log('🔍 Excel export completed');
    } catch (err: any) {
      setError(err.message || 'Failed to export Excel file');
      console.error('Error exporting Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCustomerExpansion = (customerId: string) => {
    console.log('[toggleCustomerExpansion] Called with customerId:', customerId);
    console.log(
      '[toggleCustomerExpansion] Current expandedCustomers:',
      Array.from(expandedCustomers)
    );
    console.log('[toggleCustomerExpansion] comparisonMode:', comparisonMode);
    console.log('[toggleCustomerExpansion] customDateRanges:', customDateRanges);
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
      console.log('[toggleCustomerExpansion] Removing customer, new set:', Array.from(newExpanded));
    } else {
      newExpanded.add(customerId);
      console.log('[toggleCustomerExpansion] Adding customer, new set:', Array.from(newExpanded));
    }
    setExpandedCustomers(newExpanded);
  };

  // Memoize customer monthly data lookup
  const getCustomerMonthlyData = useCallback(
    (customerId: string) => {
      const result = monthlyData.filter((month) => month.customer === customerId);
      return result;
    },
    [monthlyData, comparisonMode]
  );

  const loadMonthTransactions = useCallback(
    async (customerId: string, year: number, month: number) => {
      const monthKey = `${customerId}-${year}-${month}`;

      // Check current state using refs (synchronous check)
      if (monthTransactionsRef.current[monthKey] || loadingTransactionsRef.current.has(monthKey)) {
        return; // Already loaded or currently loading
      }

      setLoadingTransactions((prev) => {
        if (prev.has(monthKey)) {
          return prev; // Another call started loading
        }
        const newSet = new Set(prev);
        newSet.add(monthKey);
        loadingTransactionsRef.current = newSet;
        return newSet;
      });

      try {
        const response = await customerSalesComparisonService.getCustomerMonthTransactions(
          customerId,
          year,
          month
        );
        setMonthTransactions((prev) => {
          if (prev[monthKey]) {
            return prev; // Another call already loaded it
          }
          const updated = {
            ...prev,
            [monthKey]: response.data,
          };
          monthTransactionsRef.current = updated;
          return updated;
        });
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        setLoadingTransactions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(monthKey);
          loadingTransactionsRef.current = newSet;
          return newSet;
        });
      }
    },
    []
  );

  const toggleMonthExpansion = async (customerId: string, month: number) => {
    const monthKey = `${customerId}-${month}`;

    if (expandedMonths.has(monthKey)) {
      // Collapse
      setExpandedMonths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(monthKey);
        return newSet;
      });
    } else {
      // Expand and load transactions
      setExpandedMonths((prev) => new Set(prev).add(monthKey));

      if (comparisonMode === 'customRanges') {
        // For custom ranges, use the selected range's year
        const selectedRangeIndex = selectedRangeTab[customerId] ?? 0;
        const selectedRange = customDateRanges[selectedRangeIndex];
        if (selectedRange) {
          // Find month data to extract year from month name if present
          const customerMonthlyData = monthlyData.filter((m) => m.customer === customerId);
          const monthData = customerMonthlyData.find((m) => {
            const mMonthNumber = getMonthNumber(m.month);
            return mMonthNumber === month;
          });
          const monthYearMatch = monthData?.month.match(/\b(\d{4})\b/);
          const extractedYear = monthYearMatch ? parseInt(monthYearMatch[1]) : null;
          // Extract year directly from date string to avoid timezone issues
          // Date format is "YYYY-MM-DD", so extract first 4 characters
          const rangeYear = extractedYear || parseInt(selectedRange.from.substring(0, 4));
          setSelectedTransactionYear((prev) => ({
            ...prev,
            [monthKey]: rangeYear,
          }));
          await loadMonthTransactions(customerId, rangeYear, month);
        }
      } else {
        // For years mode, use latest year
        const latestYear = Math.max(...Array.from(selectedYears));
        setSelectedTransactionYear((prev) => ({
          ...prev,
          [monthKey]: latestYear,
        }));
        await loadMonthTransactions(customerId, latestYear, month);
      }
    }
  };

  const toggleTransactionYear = async (customerId: string, month: number, year: number) => {
    const monthKey = `${customerId}-${month}`;
    setSelectedTransactionYear((prev) => ({
      ...prev,
      [monthKey]: year,
    }));
    await loadMonthTransactions(customerId, year, month);
  };

  const getMonthNumber = (monthName: string): number => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months.indexOf(monthName) + 1;
  };

  // Customer selection helpers
  const allCustomerOptions = useMemo<{ id: string; name: string }[]>(() => {
    const unique = new Map<string, string>();
    // Use cached all customers for selection UI, not filtered customerData
    const sourceData = allCustomersCache.length > 0 ? allCustomersCache : customerData;
    sourceData.forEach((c) => {
      unique.set((c.customer || '').trim(), c.customerName);
    });
    return Array.from(unique.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCustomersCache, customerData]);

  const toggleCustomerSelection = (id: string) => {
    setSelectedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearCustomers = () => {
    setSelectedCustomers(new Set());
  };

  // Territory selection helpers
  const toggleTerritorySelection = (territory: string) => {
    setSelectedTerritories((prev) => {
      const next = new Set(prev);
      if (next.has(territory)) next.delete(territory);
      else next.add(territory);
      return next;
    });
  };

  const clearTerritories = () => {
    setSelectedTerritories(new Set());
  };

  // Sales person selection helpers
  const toggleSalesPersonSelection = (salesPersonId: string) => {
    setSelectedSalesPersonIds((prev) => {
      const next = new Set(prev);
      if (next.has(salesPersonId)) next.delete(salesPersonId);
      else next.add(salesPersonId);
      return next;
    });
  };

  const clearSalesPersons = () => {
    setSelectedSalesPersonIds(new Set());
  };

  // Year selection helpers
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = 2023; year <= currentYear; year++) {
      years.push(year);
    }
    return years.sort((a, b) => a - b); // Show ascending order (2023 → current)
  }, []);

  // Reset all filters to defaults
  const resetAllFilters = () => {
    const currentYear = new Date().getFullYear();
    // Reset comparison mode and custom ranges first
    setComparisonMode('years');
    setCustomDateRanges([]);
    // Then reset filter selections (useEffect hooks will update URL)
    setSelectedYears(new Set([currentYear]));
    setSelectedCustomers(new Set());
    setSelectedTerritories(new Set());
    setSelectedSalesPersonIds(new Set());
    setFilterText('');
    setCustomerFilterSearch('');
    setTerritoryFilterSearch('');
    setSalesPersonFilterSearch('');
    setYearFilterOpen(false);
    setCustomerDropdownOpen(false);
    setTerritoryDropdownOpen(false);
    setSalesPersonDropdownOpen(false);
  };

  const toggleYearSelection = (year: number) => {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
        // If this was the last year, automatically select current year
        if (next.size === 0) {
          const currentYear = new Date().getFullYear();
          next.add(currentYear);
        }
      } else {
        if (next.size >= 3) {
          // Limit to 3 years
          return prev;
        }
        next.add(year);
      }
      return next;
    });
  };

  // Memoize filtered customer data to avoid recalculating on every render
  const filteredCustomerData = useMemo(() => {
    return customerData.filter((customer) => {
      const searchText = filterText.toLowerCase();
      const inSelection =
        selectedCustomers.size === 0 || selectedCustomers.has(customer.customer.trim());
      return (
        inSelection &&
        (customer.customer.toLowerCase().includes(searchText) ||
          customer.customerName.toLowerCase().includes(searchText))
      );
    });
  }, [customerData, filterText, selectedCustomers]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredCustomerData.length / itemsPerPage);
  const paginatedCustomerData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCustomerData.slice(startIndex, endIndex);
  }, [filteredCustomerData, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, selectedCustomers, comparisonMode, selectedYears, customDateRanges]);

  // Memoize KPI calculations for years mode
  const yearRevenueKPIs = useMemo(() => {
    return Array.from(selectedYears)
      .sort((a, b) => a - b)
      .map((year, index) => {
        const kpiColors = ['kpi-orange', 'kpi-green', 'kpi-blue'];
        const colorClass = kpiColors[index] || 'kpi-orange';
        const revenue = filteredCustomerData.reduce((sum, c) => {
          const yearData = c.yearData?.[year];
          return sum + (yearData?.invoiceTotal || 0);
        }, 0);

        return {
          year,
          colorClass,
          revenue,
        };
      });
  }, [selectedYears, filteredCustomerData]);

  // Memoize growth KPI calculation
  const growthKPI = useMemo(() => {
    const yearsSorted = Array.from(selectedYears).sort((a, b) => a - b);
    if (yearsSorted.length < 2) {
      const totalDiff = filteredCustomerData.reduce((sum, c) => sum + c.totalDiff, 0);
      return {
        totalDiff,
        growthPercent: null,
        earliestYear: null,
        latestYear: null,
      };
    }
    const latestYear = yearsSorted[yearsSorted.length - 1];
    const earliestYear = yearsSorted[0];
    const latestRevenue = filteredCustomerData.reduce((sum, c) => {
      const yearData = c.yearData?.[latestYear];
      return sum + (yearData?.invoiceTotal || 0);
    }, 0);
    const earliestRevenue = filteredCustomerData.reduce((sum, c) => {
      const yearData = c.yearData?.[earliestYear];
      return sum + (yearData?.invoiceTotal || 0);
    }, 0);
    const totalDiff = latestRevenue - earliestRevenue;
    const growthPercentValue = earliestRevenue > 0 ? (totalDiff / earliestRevenue) * 100 : 0;
    return {
      totalDiff,
      growthPercent: growthPercentValue.toFixed(1),
      growthPercentValue,
      earliestYear,
      latestYear,
    };
  }, [selectedYears, filteredCustomerData]);

  // Memoize format functions to avoid recreating on every render
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const formatPercentage = useCallback((value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  }, []);

  const formatDateRangeLabel = (from: string, to: string) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const fromStr = fromDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const toStr = toDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${fromStr} – ${toStr}`;
  };

  // Check if a month overlaps with a date range
  const isMonthInRange = (monthName: string, rangeFrom: string, rangeTo: string): boolean => {
    const monthNumber = getMonthNumber(monthName);
    if (monthNumber === 0) return false; // Invalid month name

    // Parse range dates (using local time to avoid timezone issues)
    const fromDate = new Date(rangeFrom);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(rangeTo);
    toDate.setHours(23, 59, 59, 999);

    // Get year from month name if it includes year, otherwise check all years in range
    const monthYearMatch = monthName.match(/\b(\d{4})\b/);

    if (monthYearMatch) {
      // Month has year specified (e.g., "November 2025")
      const monthYear = parseInt(monthYearMatch[1]);
      const monthStart = new Date(monthYear, monthNumber - 1, 1);
      const monthEnd = new Date(monthYear, monthNumber, 0, 23, 59, 59);
      // Check if month overlaps with range
      return monthStart <= toDate && monthEnd >= fromDate;
    } else {
      // Month name without year (e.g., "November") - check all years in the range
      const fromYear = fromDate.getFullYear();
      const toYear = toDate.getFullYear();

      for (let year = fromYear; year <= toYear; year++) {
        const monthStart = new Date(year, monthNumber - 1, 1);
        const monthEnd = new Date(year, monthNumber, 0, 23, 59, 59);
        // Check if month overlaps with range
        if (monthStart <= toDate && monthEnd >= fromDate) {
          return true;
        }
      }
      return false;
    }
  };

  // Update URL params when selections change
  useEffect(() => {
    if (selectedYears.size > 0) {
      updateUrlParams({
        years: Array.from(selectedYears)
          .sort((a, b) => a - b)
          .join(','),
      });
    } else {
      updateUrlParams({ years: null });
    }
  }, [selectedYears, updateUrlParams]);

  useEffect(() => {
    if (selectedCustomers.size > 0) {
      updateUrlParams({
        customers: Array.from(selectedCustomers).join(','),
      });
    } else {
      updateUrlParams({ customers: null });
    }
  }, [selectedCustomers, updateUrlParams]);

  useEffect(() => {
    if (selectedTerritories.size > 0) {
      updateUrlParams({
        territories: Array.from(selectedTerritories).join(','),
      });
    } else {
      updateUrlParams({ territories: null });
    }
  }, [selectedTerritories, updateUrlParams]);

  useEffect(() => {
    if (selectedSalesPersonIds.size > 0) {
      updateUrlParams({
        salesPersons: Array.from(selectedSalesPersonIds).join(','),
      });
    } else {
      updateUrlParams({ salesPersons: null });
    }
  }, [selectedSalesPersonIds, updateUrlParams]);

  useEffect(() => {
    updateUrlParams({ mode: comparisonMode });
  }, [comparisonMode, updateUrlParams]);

  // Pre-load transactions for all visible months when customer is expanded (custom ranges mode)
  useEffect(() => {
    if (
      comparisonMode !== 'customRanges' ||
      expandedCustomers.size === 0 ||
      monthlyData.length === 0
    )
      return;

    const loadTransactionsForExpandedCustomers = async () => {
      const loadPromises: Promise<void>[] = [];

      for (const customerId of expandedCustomers) {
        const customerMonthlyData = monthlyData.filter((m) => m.customer === customerId);
        if (customerMonthlyData.length === 0) continue;

        const selectedRangeIndex = selectedRangeTab[customerId] ?? 0;
        const selectedRange = customDateRanges[selectedRangeIndex];
        if (!selectedRange) continue;

        const rangeYear = new Date(selectedRange.from).getFullYear();

        // Load transactions for all visible months (loadMonthTransactions handles duplicate checks)
        for (const month of customerMonthlyData) {
          if (isMonthInRange(month.month, selectedRange.from, selectedRange.to)) {
            const monthNumber = getMonthNumber(month.month);
            // loadMonthTransactions checks current state using refs, so safe to call
            loadPromises.push(loadMonthTransactions(customerId, rangeYear, monthNumber));
          }
        }
      }

      // Load all in parallel
      await Promise.all(loadPromises);
    };

    loadTransactionsForExpandedCustomers();
  }, [
    expandedCustomers,
    comparisonMode,
    monthlyData,
    customDateRanges,
    selectedRangeTab,
    loadMonthTransactions,
  ]);

  useEffect(() => {
    if (customDateRanges.length > 0) {
      updateUrlParams({
        dateRanges: encodeURIComponent(JSON.stringify(customDateRanges)),
      });
    } else {
      updateUrlParams({ dateRanges: null });
    }
  }, [customDateRanges, updateUrlParams]);

  // Sync URL params on mount if defaults need to be set
  useEffect(() => {
    const hasAnyParams =
      searchParams.has('years') ||
      searchParams.has('customers') ||
      searchParams.has('territories') ||
      searchParams.has('salesPersons') ||
      searchParams.has('mode') ||
      searchParams.has('dateRanges');

    if (!hasAnyParams) {
      // Set default year in URL if no params exist
      const currentYear = new Date().getFullYear();
      updateUrlParams({ years: currentYear.toString(), mode: 'years' });
    }
  }, []); // Only run on mount

  useEffect(() => {
    if (comparisonMode === 'years' && selectedYears.size > 0) {
      loadData();
    } else if (comparisonMode === 'customRanges' && customDateRanges.length > 0) {
      // Validate ranges before loading
      const invalidRange = customDateRanges.find((r) => !r.from || !r.to || r.from > r.to);
      if (!invalidRange) {
        loadData();
      }
    }
  }, [
    selectedYears,
    selectedCustomers,
    selectedTerritories,
    selectedSalesPersonIds,
    comparisonMode,
    customDateRanges,
  ]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await customerSalesComparisonService.getFilterOptions();
        if (response.success && response.data) {
          setAvailableTerritories(response.data.territories || []);
          setAvailableSalesPersonIds(response.data.salesPersonIds || []);
        }
      } catch (err) {
        console.warn('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Clear cache when years change
  useEffect(() => {
    setAllCustomersCache([]);
  }, [selectedYears]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (yearFilterOpen && !target.closest('[data-year-dropdown]')) {
        setYearFilterOpen(false);
      }
      if (customerDropdownOpen && !target.closest('[data-customer-dropdown]')) {
        setCustomerDropdownOpen(false);
      }
      if (territoryDropdownOpen && !target.closest('[data-territory-dropdown]')) {
        setTerritoryDropdownOpen(false);
      }
      if (salesPersonDropdownOpen && !target.closest('[data-salesperson-dropdown]')) {
        setSalesPersonDropdownOpen(false);
      }
    };

    if (
      yearFilterOpen ||
      customerDropdownOpen ||
      territoryDropdownOpen ||
      salesPersonDropdownOpen
    ) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [yearFilterOpen, customerDropdownOpen, territoryDropdownOpen, salesPersonDropdownOpen]);

  return (
    <div className="sales-reports-page">
      <div className="sales-reports-container">
        {/* Header */}
        <div className="sales-reports-header">
          <div>
            <h1 className="sales-reports-title">Sales Reports</h1>
            <p className="sales-reports-subtitle">
              Year-over-year customer sales analysis with monthly breakdowns
            </p>
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="card mb-6 filter-bar-card" style={{ position: 'relative', zIndex: 100 }}>
          <div className="card-body">
            <div
              className="filter-bar"
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              {/* Row 1: Years, Customers, Territories, Sales Persons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
                {/* Years - compact dropdown (only show when in years mode) */}
                {comparisonMode === 'years' && (
                  <div className="filter-group">
                    <div style={{ position: 'relative' }} data-year-dropdown>
                      <button
                        onClick={() => {
                          setYearFilterOpen((v) => !v);
                        }}
                        className="btn btn-sm filter-btn"
                      >
                        <span>Years ({selectedYears.size}/3)</span>
                        <svg
                          className={`filter-arrow transition-transform ${yearFilterOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {yearFilterOpen && (
                        <div
                          className="filter-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: '0.5rem',
                            zIndex: 5000,
                            minWidth: 200,
                          }}
                          data-year-dropdown
                          ref={(_el) => {
                            // Ref callback
                          }}
                        >
                          <div className="filter-dropdown-header">
                            <span>Select Years (up to 3)</span>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <span className="filter-count">
                                {selectedYears.size}/{availableYears.length}
                              </span>
                            </div>
                          </div>
                          <div className="filter-dropdown-content">
                            {availableYears.map((year) => {
                              const isSelected = selectedYears.has(year);
                              const isDisabled = !isSelected && selectedYears.size >= 3;
                              return (
                                <label
                                  key={year}
                                  className={`filter-dropdown-item ${isDisabled ? 'disabled' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleYearSelection(year)}
                                    disabled={isDisabled}
                                  />
                                  <span>
                                    {year}
                                    {year === new Date().getFullYear() && (
                                      <span className="text-xs text-blue-600 ml-1">(Current)</span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customers - compact dropdown */}
                <div className="filter-group">
                  <div style={{ position: 'relative' }} data-customer-dropdown>
                    <button
                      onClick={() => {
                        setCustomerDropdownOpen((v) => !v);
                      }}
                      className="btn btn-sm filter-btn"
                    >
                      <span>Customers ({selectedCustomers.size || 'All'})</span>
                      <svg
                        className={`filter-arrow transition-transform ${customerDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {customerDropdownOpen && (
                      <div
                        className="filter-dropdown filter-dropdown-large"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '0.5rem',
                          zIndex: 5000,
                          minWidth: 400,
                        }}
                        data-customer-dropdown
                      >
                        <div className="filter-dropdown-header">
                          <div className="input-with-icon" style={{ width: '100%' }}>
                            <div className="input-icon">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              placeholder="Search customers..."
                              value={customerFilterSearch}
                              onChange={(e) => setCustomerFilterSearch(e.target.value)}
                              className="input"
                              autoFocus
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button onClick={clearCustomers} className="btn btn-xs btn-secondary">
                              Clear
                            </button>
                            <span className="filter-count">
                              {selectedCustomers.size}/{allCustomerOptions.length}
                            </span>
                          </div>
                        </div>
                        <div className="filter-dropdown-content" style={{ maxHeight: 300 }}>
                          {allCustomerOptions
                            .filter(
                              (o) =>
                                !customerFilterSearch ||
                                o.id.toLowerCase().includes(customerFilterSearch.toLowerCase()) ||
                                o.name.toLowerCase().includes(customerFilterSearch.toLowerCase())
                            )
                            .map((opt) => {
                              const checked = selectedCustomers.has(opt.id);
                              return (
                                <label key={opt.id} className="filter-dropdown-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCustomerSelection(opt.id)}
                                  />
                                  <span>
                                    <span className="font-medium">{opt.name}</span>
                                    <span className="text-xs font-mono text-gray-500 ml-1">
                                      {opt.id}
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          {allCustomerOptions.length === 0 && (
                            <div className="filter-dropdown-empty">No customers available</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Territories - compact dropdown */}
                <div className="filter-group">
                  <div style={{ position: 'relative' }} data-territory-dropdown>
                    <button
                      onClick={() => {
                        setTerritoryDropdownOpen((v) => !v);
                      }}
                      className="btn btn-sm filter-btn"
                    >
                      <span>Territories ({selectedTerritories.size || 'All'})</span>
                      <svg
                        className={`filter-arrow transition-transform ${territoryDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {territoryDropdownOpen && (
                      <div
                        className="filter-dropdown"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '0.5rem',
                          zIndex: 5000,
                          minWidth: 250,
                        }}
                        data-territory-dropdown
                      >
                        <div className="filter-dropdown-header">
                          <div className="input-with-icon" style={{ width: '100%' }}>
                            <div className="input-icon">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              placeholder="Search territories..."
                              value={territoryFilterSearch}
                              onChange={(e) => setTerritoryFilterSearch(e.target.value)}
                              className="input"
                              autoFocus
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button onClick={clearTerritories} className="btn btn-xs btn-secondary">
                              Clear
                            </button>
                            <span className="filter-count">
                              {selectedTerritories.size}/{availableTerritories.length}
                            </span>
                          </div>
                        </div>
                        <div className="filter-dropdown-content" style={{ maxHeight: 300 }}>
                          {availableTerritories
                            .filter(
                              (t) =>
                                !territoryFilterSearch ||
                                t.toLowerCase().includes(territoryFilterSearch.toLowerCase())
                            )
                            .map((territory) => {
                              const checked = selectedTerritories.has(territory);
                              return (
                                <label key={territory} className="filter-dropdown-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTerritorySelection(territory)}
                                  />
                                  <span>{territory}</span>
                                </label>
                              );
                            })}
                          {availableTerritories.length === 0 && (
                            <div className="filter-dropdown-empty">No territories available</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sales Persons - compact dropdown */}
                <div className="filter-group">
                  <div style={{ position: 'relative' }} data-salesperson-dropdown>
                    <button
                      onClick={() => {
                        setSalesPersonDropdownOpen((v) => !v);
                      }}
                      className="btn btn-sm filter-btn"
                    >
                      <span>Sales Persons ({selectedSalesPersonIds.size || 'All'})</span>
                      <svg
                        className={`filter-arrow transition-transform ${salesPersonDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {salesPersonDropdownOpen && (
                      <div
                        className="filter-dropdown"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '0.5rem',
                          zIndex: 5000,
                          minWidth: 250,
                        }}
                        data-salesperson-dropdown
                      >
                        <div className="filter-dropdown-header">
                          <div className="input-with-icon" style={{ width: '100%' }}>
                            <div className="input-icon">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              placeholder="Search sales person IDs..."
                              value={salesPersonFilterSearch}
                              onChange={(e) => setSalesPersonFilterSearch(e.target.value)}
                              className="input"
                              autoFocus
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button
                              onClick={clearSalesPersons}
                              className="btn btn-xs btn-secondary"
                            >
                              Clear
                            </button>
                            <span className="filter-count">
                              {selectedSalesPersonIds.size}/{availableSalesPersonIds.length}
                            </span>
                          </div>
                        </div>
                        <div className="filter-dropdown-content" style={{ maxHeight: 300 }}>
                          {availableSalesPersonIds
                            .filter(
                              (sp) =>
                                !salesPersonFilterSearch ||
                                sp.toLowerCase().includes(salesPersonFilterSearch.toLowerCase())
                            )
                            .map((salesPersonId) => {
                              const checked = selectedSalesPersonIds.has(salesPersonId);
                              return (
                                <label key={salesPersonId} className="filter-dropdown-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSalesPersonSelection(salesPersonId)}
                                  />
                                  <span>{salesPersonId}</span>
                                </label>
                              );
                            })}
                          {availableSalesPersonIds.length === 0 && (
                            <div className="filter-dropdown-empty">
                              No sales person IDs available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Comparison Mode Toggle + Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                {/* Comparison Mode Toggle */}
                <div className="filter-group">
                  <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Compare:</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => setComparisonMode('years')}
                      className={`btn btn-sm ${comparisonMode === 'years' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    >
                      Years
                    </button>
                    <button
                      onClick={() => setComparisonMode('customRanges')}
                      className={`btn btn-sm ${comparisonMode === 'customRanges' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    >
                      Custom Ranges
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div
                  className="filter-group filter-actions"
                  style={{ display: 'flex', gap: '0.5rem' }}
                >
                  <button
                    onClick={loadData}
                    disabled={dataLoading}
                    className="btn btn-sm"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dataLoading ? (
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    )}
                    <span>Run Report</span>
                  </button>
                  <button
                    onClick={handleExportExcel}
                    disabled={loading || customerData.length === 0}
                    className="btn btn-sm btn-icon btn-accent"
                    aria-label="Export Excel"
                    title="Export Excel"
                  >
                    {loading ? (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={resetAllFilters}
                    className="btn btn-sm btn-icon btn-secondary"
                    aria-label="Reset All Filters"
                    title="Reset All Filters"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Custom Date Ranges (only show when in customRanges mode) - separate section */}
              {comparisonMode === 'customRanges' && (
                <div className="filter-group" style={{ width: '100%' }}>
                  <label style={{ marginBottom: '0.5rem', fontWeight: '500', display: 'block' }}>
                    Custom Date Ranges (up to 3)
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {customDateRanges.map((range, index) => {
                      const isValid = range.from && range.to && range.from <= range.to;
                      return (
                        <div
                          key={index}
                          style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'flex-start',
                              padding: '0.5rem',
                              border: `1px solid ${isValid ? '#ced4da' : '#dc3545'}`,
                              borderRadius: '4px',
                              backgroundColor: isValid ? '#fff' : '#fff5f5',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '0.875rem',
                                  marginBottom: '0.25rem',
                                  color: isValid ? '#495057' : '#dc3545',
                                }}
                              >
                                From
                              </label>
                              <input
                                type="date"
                                value={range.from}
                                onChange={(e) => {
                                  const newRanges = [...customDateRanges];
                                  newRanges[index] = { ...newRanges[index], from: e.target.value };
                                  setCustomDateRanges(newRanges);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.375rem',
                                  border: `1px solid ${isValid ? '#ced4da' : '#dc3545'}`,
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '0.875rem',
                                  marginBottom: '0.25rem',
                                  color: isValid ? '#495057' : '#dc3545',
                                }}
                              >
                                To
                              </label>
                              <input
                                type="date"
                                value={range.to}
                                onChange={(e) => {
                                  const newRanges = [...customDateRanges];
                                  newRanges[index] = { ...newRanges[index], to: e.target.value };
                                  setCustomDateRanges(newRanges);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.375rem',
                                  border: `1px solid ${isValid ? '#ced4da' : '#dc3545'}`,
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                }}
                              />
                            </div>
                            <button
                              onClick={() => {
                                const newRanges = customDateRanges.filter((_, i) => i !== index);
                                setCustomDateRanges(newRanges);
                              }}
                              className="btn btn-sm btn-outline-danger"
                              style={{ marginTop: '1.5rem' }}
                              title="Remove range"
                            >
                              ×
                            </button>
                          </div>
                          {!isValid && (
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: '#dc3545',
                                paddingLeft: '0.5rem',
                              }}
                            >
                              Invalid range
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {customDateRanges.length === 0 && (
                      <div style={{ fontSize: '0.875rem', color: '#6c757d', fontStyle: 'italic' }}>
                        No date ranges added. Click "Add Range" below to add one.
                      </div>
                    )}
                    {customDateRanges.length < 3 && (
                      <button
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          const currentYear = new Date().getFullYear();
                          const defaultFrom = `${currentYear}-01-01`;
                          setCustomDateRanges([
                            ...customDateRanges,
                            { from: defaultFrom, to: today },
                          ]);
                        }}
                        className="btn btn-sm btn-outline-primary"
                        style={{ alignSelf: 'flex-start' }}
                      >
                        + Add Range
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        {customerData.length > 0 && (
          <div className="kpi-grid">
            {comparisonMode === 'years' ? (
              <>
                {/* Year Revenue Cards - Dynamic based on selected years */}
                {yearRevenueKPIs.map(({ year, colorClass, revenue }) => (
                  <div key={year} className={`kpi ${colorClass}`}>
                    <div className="kpi-content">
                      <div className="kpi-text">
                        <p className="kpi-label">{year} Revenue</p>
                        <p className="kpi-value">{formatCurrency(revenue)}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Growth */}
                <div className="kpi kpi-purple">
                  <div className="kpi-content">
                    <div className="kpi-text">
                      <p className="kpi-label">Growth</p>
                      {growthKPI.growthPercent === null ? (
                        <p className="kpi-value">{formatCurrency(growthKPI.totalDiff)}</p>
                      ) : (
                        <>
                          <p className="kpi-value">{formatCurrency(growthKPI.totalDiff)}</p>
                          <p className="kpi-subtitle">
                            {growthKPI.growthPercentValue! >= 0 ? '+' : ''}
                            {growthKPI.growthPercent}% ({growthKPI.earliestYear}→
                            {growthKPI.latestYear})
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Custom Date Range Revenue Cards */}
                {customDateRanges.map((range, index) => {
                  const kpiColors = ['kpi-orange', 'kpi-green', 'kpi-blue'];
                  const colorClass = kpiColors[index] || 'kpi-orange';
                  const rangeKey = `${range.from}_${range.to}`;
                  const revenue = filteredCustomerData.reduce((sum, c) => {
                    const rangeData = c.rangeData?.[rangeKey];
                    return sum + (rangeData?.invoiceTotal || 0);
                  }, 0);

                  return (
                    <div key={rangeKey} className={`kpi ${colorClass}`}>
                      <div className="kpi-content">
                        <div className="kpi-text">
                          <p className="kpi-label" style={{ fontSize: '0.75rem' }}>
                            {formatDateRangeLabel(range.from, range.to)}
                          </p>
                          <p className="kpi-value">{formatCurrency(revenue)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Growth */}
                <div className="kpi kpi-purple">
                  <div className="kpi-content">
                    <div className="kpi-text">
                      <p className="kpi-label">Growth</p>
                      {(() => {
                        if (customDateRanges.length < 2) {
                          const totalDiff = filteredCustomerData.reduce(
                            (sum, c) => sum + c.totalDiff,
                            0
                          );
                          return <p className="kpi-value">{formatCurrency(totalDiff)}</p>;
                        }
                        // Calculate range-to-range growth
                        const latestRange = customDateRanges[customDateRanges.length - 1];
                        const earliestRange = customDateRanges[0];
                        const latestRangeKey = `${latestRange.from}_${latestRange.to}`;
                        const earliestRangeKey = `${earliestRange.from}_${earliestRange.to}`;
                        const latestRevenue = filteredCustomerData.reduce((sum, c) => {
                          const rangeData = c.rangeData?.[latestRangeKey];
                          return sum + (rangeData?.invoiceTotal || 0);
                        }, 0);
                        const earliestRevenue = filteredCustomerData.reduce((sum, c) => {
                          const rangeData = c.rangeData?.[earliestRangeKey];
                          return sum + (rangeData?.invoiceTotal || 0);
                        }, 0);
                        const totalDiff = latestRevenue - earliestRevenue;
                        const growthPercentValue =
                          earliestRevenue > 0 ? (totalDiff / earliestRevenue) * 100 : 0;
                        const growthPercent = growthPercentValue.toFixed(1);
                        return (
                          <>
                            <p className="kpi-value">{formatCurrency(totalDiff)}</p>
                            <p className="kpi-subtitle" style={{ fontSize: '0.75rem' }}>
                              {growthPercentValue >= 0 ? '+' : ''}
                              {growthPercent}%
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Customer Sales Table */}
        <div className="table-card">
          <div className="table-header">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="table-title">Customer Sales Comparison</h3>
                <p className="table-subtitle">Click on a customer row to view monthly breakdown</p>
              </div>
              <div className="table-actions">
                <div className="input-with-icon">
                  <div className="input-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="input"
                  />
                </div>
                <div className="text-sm text-ink-600">
                  <span className="font-medium text-ink-900">{filteredCustomerData.length}</span>
                  <span className="mx-1"> of </span>
                  <span className="font-medium text-ink-900">{customerData.length}</span>
                  <span className="ml-1"> customers</span>
                </div>
              </div>
            </div>
          </div>
          <div className="table-wrap">
            {dataLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                  <span className="text-gray-600 font-medium">Loading customer data...</span>
                </div>
              </div>
            ) : customerData.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-8 w-8 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No customer data</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Click "Refresh Data" to load customer sales information.
                </p>
              </div>
            ) : filteredCustomerData.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-8 w-8 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
                <p className="mt-1 text-sm text-gray-500">Try adjusting your search terms.</p>
              </div>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead className="thead sticky top-0 z-10 bg-white shadow-sm">
                    <tr>
                      <th className="th">Customer</th>
                      {comparisonMode === 'years' ? (
                        <>
                          {/* All Doc counts together */}
                          {Array.from(selectedYears)
                            .sort((a, b) => a - b)
                            .map((year) => (
                              <th key={year} className="th text-right">
                                {year} Docs
                              </th>
                            ))}
                          {/* All Revenue together */}
                          {Array.from(selectedYears)
                            .sort((a, b) => a - b)
                            .map((year) => (
                              <th key={year} className="th text-right">
                                {year} Revenue
                              </th>
                            ))}
                          {/* Growth columns between consecutive years */}
                          {Array.from(selectedYears)
                            .sort((a, b) => a - b)
                            .slice(1)
                            .map((year, index) => {
                              const prevYear = Array.from(selectedYears).sort((a, b) => a - b)[
                                index
                              ];
                              return (
                                <React.Fragment key={`growth-${prevYear}-${year}`}>
                                  <th className="th text-right">
                                    {prevYear}→{year} Growth Amount $
                                  </th>
                                  <th className="th text-right">
                                    {prevYear}→{year} Growth %
                                  </th>
                                </React.Fragment>
                              );
                            })}
                        </>
                      ) : (
                        <>
                          {/* Custom Date Range Doc counts */}
                          {customDateRanges.map((range) => {
                            const rangeKey = `${range.from}_${range.to}`;
                            return (
                              <th
                                key={`docs-${rangeKey}`}
                                className="th text-right"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {formatDateRangeLabel(range.from, range.to)} Docs
                              </th>
                            );
                          })}
                          {/* Custom Date Range Revenue */}
                          {customDateRanges.map((range) => {
                            const rangeKey = `${range.from}_${range.to}`;
                            return (
                              <th
                                key={`revenue-${rangeKey}`}
                                className="th text-right"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {formatDateRangeLabel(range.from, range.to)} Revenue
                              </th>
                            );
                          })}
                          {/* Growth columns between consecutive ranges */}
                          {(() => {
                            const sortedRanges = [...customDateRanges].sort((a, b) =>
                              a.from.localeCompare(b.from)
                            );
                            return sortedRanges.slice(1).map((range, index) => {
                              const prevRange = sortedRanges[index];
                              const prevRangeKey = `${prevRange.from}_${prevRange.to}`;
                              const rangeKey = `${range.from}_${range.to}`;
                              const rangeNumber1 = index + 1;
                              const rangeNumber2 = index + 2;
                              return (
                                <React.Fragment key={`growth-${prevRangeKey}-${rangeKey}`}>
                                  <th className="th text-right" style={{ fontSize: '0.75rem' }}>
                                    Range {rangeNumber1}-{rangeNumber2} Growth Amount $
                                  </th>
                                  <th className="th text-right" style={{ fontSize: '0.75rem' }}>
                                    Range {rangeNumber1}-{rangeNumber2} Growth %
                                  </th>
                                </React.Fragment>
                              );
                            });
                          })()}
                        </>
                      )}
                      {(comparisonMode === 'years' && selectedYears.size > 0) ||
                      (comparisonMode === 'customRanges' && customDateRanges.length > 0) ? (
                        <th className="th text-right">% of Total</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y border-primary-200">
                    {paginatedCustomerData.map((customer, index) => {
                      const isExpanded = expandedCustomers.has(customer.customer);
                      const monthlyData = getCustomerMonthlyData(customer.customer);

                      if (index === 0) {
                        console.log('[Row Render] First customer:', customer.customer);
                        console.log('[Row Render] isExpanded:', isExpanded);
                        console.log('[Row Render] monthlyData.length:', monthlyData.length);
                        console.log('[Row Render] comparisonMode:', comparisonMode);
                        console.log(
                          '[Row Render] customDateRanges.length:',
                          customDateRanges.length
                        );
                        console.log(
                          '[Row Render] chevron class:',
                          `chevron ${isExpanded ? 'expanded' : ''}`
                        );
                      }

                      return (
                        <React.Fragment key={`${customer.customer}-${index}`}>
                          <tr
                            className="tr"
                            onClick={() => toggleCustomerExpansion(customer.customer)}
                          >
                            <td className="td">
                              <div className="flex items-center gap-4">
                                <div className={`chevron ${isExpanded ? 'expanded' : ''}`}>
                                  <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium truncate text-ink-900">
                                    {customer.customerName}
                                  </div>
                                  <div className="text-xs font-mono text-ink-600">
                                    {customer.customer.trim()}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {comparisonMode === 'years' ? (
                              <>
                                {/* All Doc counts together */}
                                {Array.from(selectedYears)
                                  .sort((a, b) => a - b)
                                  .map((year) => {
                                    const yearData = customer.yearData?.[year];
                                    return (
                                      <td
                                        key={`docs-${customer.customer}-${year}`}
                                        className="td text-right"
                                      >
                                        <span className="font-medium">
                                          {yearData?.documentCount || 0}
                                        </span>
                                      </td>
                                    );
                                  })}
                                {/* All Revenue together */}
                                {Array.from(selectedYears)
                                  .sort((a, b) => a - b)
                                  .map((year) => {
                                    const yearData = customer.yearData?.[year];
                                    return (
                                      <td
                                        key={`revenue-${customer.customer}-${year}`}
                                        className="td text-right"
                                      >
                                        <span className="font-medium">
                                          {formatCurrency(yearData?.invoiceTotal || 0)}
                                        </span>
                                      </td>
                                    );
                                  })}
                                {/* Growth columns between consecutive years */}
                                {Array.from(selectedYears)
                                  .sort((a, b) => a - b)
                                  .slice(1)
                                  .map((year, index) => {
                                    const prevYear = Array.from(selectedYears).sort(
                                      (a, b) => a - b
                                    )[index];
                                    const prevYearData = customer.yearData?.[prevYear] || {
                                      year: prevYear,
                                      documentCount: 0,
                                      invoiceTotal: 0,
                                    };
                                    const currentYearData = customer.yearData?.[year] || {
                                      year,
                                      documentCount: 0,
                                      invoiceTotal: 0,
                                    };
                                    const diff =
                                      currentYearData.invoiceTotal - prevYearData.invoiceTotal;
                                    const growthPercent =
                                      prevYearData.invoiceTotal > 0
                                        ? (diff / prevYearData.invoiceTotal) * 100
                                        : 0;
                                    const isPositive = diff >= 0;
                                    return (
                                      <React.Fragment
                                        key={`growth-${customer.customer}-${prevYear}-${year}`}
                                      >
                                        <td className="td text-right">
                                          <span
                                            className={`badge ${isPositive ? 'badge-green' : 'badge-red'}`}
                                          >
                                            {formatCurrency(diff)}
                                          </span>
                                        </td>
                                        <td className="td text-right">
                                          <span
                                            className={`badge ${isPositive ? 'badge-green' : 'badge-red'}`}
                                          >
                                            {growthPercent >= 0 ? '+' : ''}
                                            {growthPercent.toFixed(1)}%
                                          </span>
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                              </>
                            ) : (
                              <>
                                {/* Custom Date Range Doc counts */}
                                {(() => {
                                  const sortedRanges = [...customDateRanges].sort((a, b) =>
                                    a.from.localeCompare(b.from)
                                  );
                                  return sortedRanges.map((range) => {
                                    const rangeKey = `${range.from}_${range.to}`;
                                    const rangeData = customer.rangeData?.[rangeKey];
                                    return (
                                      <td
                                        key={`docs-${customer.customer}-${rangeKey}`}
                                        className="td text-right"
                                      >
                                        <span className="font-medium">
                                          {rangeData?.documentCount || 0}
                                        </span>
                                      </td>
                                    );
                                  });
                                })()}
                                {/* Custom Date Range Revenue */}
                                {(() => {
                                  const sortedRanges = [...customDateRanges].sort((a, b) =>
                                    a.from.localeCompare(b.from)
                                  );
                                  return sortedRanges.map((range) => {
                                    const rangeKey = `${range.from}_${range.to}`;
                                    const rangeData = customer.rangeData?.[rangeKey];
                                    return (
                                      <td
                                        key={`revenue-${customer.customer}-${rangeKey}`}
                                        className="td text-right"
                                      >
                                        <span className="font-medium">
                                          {formatCurrency(rangeData?.invoiceTotal || 0)}
                                        </span>
                                      </td>
                                    );
                                  });
                                })()}
                                {/* Growth columns between consecutive ranges */}
                                {(() => {
                                  const sortedRanges = [...customDateRanges].sort((a, b) =>
                                    a.from.localeCompare(b.from)
                                  );
                                  return sortedRanges.slice(1).map((range, index) => {
                                    const prevRange = sortedRanges[index];
                                    const prevRangeKey = `${prevRange.from}_${prevRange.to}`;
                                    const rangeKey = `${range.from}_${range.to}`;
                                    const prevRangeData = customer.rangeData?.[prevRangeKey] || {
                                      rangeKey: prevRangeKey,
                                      from: prevRange.from,
                                      to: prevRange.to,
                                      documentCount: 0,
                                      invoiceTotal: 0,
                                    };
                                    const currentRangeData = customer.rangeData?.[rangeKey] || {
                                      rangeKey,
                                      from: range.from,
                                      to: range.to,
                                      documentCount: 0,
                                      invoiceTotal: 0,
                                    };
                                    const diff =
                                      currentRangeData.invoiceTotal - prevRangeData.invoiceTotal;
                                    const growthPercent =
                                      prevRangeData.invoiceTotal > 0
                                        ? (diff / prevRangeData.invoiceTotal) * 100
                                        : 0;
                                    const isPositive = diff >= 0;
                                    return (
                                      <React.Fragment
                                        key={`growth-${customer.customer}-${prevRangeKey}-${rangeKey}`}
                                      >
                                        <td className="td text-right">
                                          <span
                                            className={`badge ${isPositive ? 'badge-green' : 'badge-red'}`}
                                          >
                                            {formatCurrency(diff)}
                                          </span>
                                        </td>
                                        <td className="td text-right">
                                          <span
                                            className={`badge ${isPositive ? 'badge-green' : 'badge-red'}`}
                                          >
                                            {growthPercent >= 0 ? '+' : ''}
                                            {growthPercent.toFixed(1)}%
                                          </span>
                                        </td>
                                      </React.Fragment>
                                    );
                                  });
                                })()}
                              </>
                            )}
                            {(comparisonMode === 'years' && selectedYears.size > 0) ||
                            (comparisonMode === 'customRanges' && customDateRanges.length > 0) ? (
                              <td className="td text-right">
                                <span className="font-medium">
                                  {formatPercentage(customer.percentOfYearlySales)}
                                </span>
                              </td>
                            ) : null}
                          </tr>

                          {/* Monthly Breakdown */}
                          {isExpanded &&
                            monthlyData.length > 0 &&
                            (() => {
                              const customerMonthlyData = monthlyData.filter(
                                (m) => m.customer === customer.customer
                              );
                              const selectedRangeIndex = selectedRangeTab[customer.customer] ?? 0;
                              const selectedRange =
                                comparisonMode === 'customRanges'
                                  ? customDateRanges[selectedRangeIndex]
                                  : null;

                              // Filter months to only show those within the selected range (for custom ranges)
                              const filteredMonthlyData =
                                comparisonMode === 'customRanges' && selectedRange
                                  ? customerMonthlyData.filter((month) => {
                                      return isMonthInRange(
                                        month.month,
                                        selectedRange.from,
                                        selectedRange.to
                                      );
                                    })
                                  : customerMonthlyData;

                              return (
                                <tr className="monthly-breakdown">
                                  <td
                                    colSpan={
                                      comparisonMode === 'years'
                                        ? 1 +
                                          selectedYears.size * 2 +
                                          (selectedYears.size - 1) * 2 +
                                          1
                                        : 1 +
                                          customDateRanges.length * 2 +
                                          (customDateRanges.length - 1) * 2 +
                                          1
                                    }
                                    className="px-6 py-6"
                                  >
                                    <div className="monthly-breakdown-card">
                                      <div className="monthly-breakdown-header">
                                        <h4 className="monthly-breakdown-title">
                                          <svg
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                          </svg>
                                          Monthly Breakdown
                                        </h4>
                                      </div>

                                      {/* Range Tabs (only for custom ranges) */}
                                      {comparisonMode === 'customRanges' &&
                                        customDateRanges.length > 1 && (
                                          <div className="mb-4 border-b border-gray-200">
                                            <div className="flex gap-2">
                                              {customDateRanges.map((range, rangeIndex) => {
                                                const rangeKey = `${range.from}_${range.to}`;
                                                const isSelected =
                                                  selectedRangeIndex === rangeIndex;
                                                return (
                                                  <button
                                                    key={rangeKey}
                                                    onClick={() =>
                                                      setSelectedRangeTab((prev) => ({
                                                        ...prev,
                                                        [customer.customer]: rangeIndex,
                                                      }))
                                                    }
                                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                                      isSelected
                                                        ? 'border-blue-500 text-blue-600'
                                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                    }`}
                                                  >
                                                    {formatDateRangeLabel(range.from, range.to)}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                      <div className="table-scroll">
                                        <table className="table">
                                          <thead className="thead sticky top-0 z-10 bg-white shadow-sm">
                                            <tr>
                                              <th className="th">Month</th>
                                              {comparisonMode === 'years' ? (
                                                <>
                                                  {/* All Doc counts together */}
                                                  {Array.from(selectedYears)
                                                    .sort((a, b) => a - b)
                                                    .map((year) => (
                                                      <th key={year} className="th text-right">
                                                        {year} Docs
                                                      </th>
                                                    ))}
                                                  {/* All Revenue together */}
                                                  {Array.from(selectedYears)
                                                    .sort((a, b) => a - b)
                                                    .map((year) => (
                                                      <th key={year} className="th text-right">
                                                        {year} Revenue
                                                      </th>
                                                    ))}
                                                  {/* Growth columns between consecutive years */}
                                                  {Array.from(selectedYears)
                                                    .sort((a, b) => a - b)
                                                    .slice(1)
                                                    .map((year, index) => {
                                                      const prevYear = Array.from(
                                                        selectedYears
                                                      ).sort((a, b) => a - b)[index];
                                                      return (
                                                        <th
                                                          key={`growth-${prevYear}-${year}`}
                                                          className="th text-right"
                                                        >
                                                          {prevYear}→{year} Growth
                                                        </th>
                                                      );
                                                    })}
                                                </>
                                              ) : (
                                                <>
                                                  {/* Show only selected range data */}
                                                  {selectedRange && (
                                                    <>
                                                      <th className="th text-right">Docs</th>
                                                      <th className="th text-right">Revenue</th>
                                                    </>
                                                  )}
                                                </>
                                              )}
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y border-primary-200">
                                            {filteredMonthlyData.map((month, index) => {
                                              const monthNumber = getMonthNumber(month.month);
                                              const monthKey = `${customer.customer}-${monthNumber}`;

                                              // For custom ranges, use the selected range for transaction loading
                                              let selectedYear = 0;
                                              let transactionKey = '';
                                              if (
                                                comparisonMode === 'customRanges' &&
                                                selectedRange
                                              ) {
                                                // Extract year from month name if present, otherwise from date string
                                                const monthYearMatch =
                                                  month.month.match(/\b(\d{4})\b/);
                                                const extractedYear = monthYearMatch
                                                  ? parseInt(monthYearMatch[1])
                                                  : null;
                                                // Extract year directly from date string to avoid timezone issues
                                                // Date format is "YYYY-MM-DD", so extract first 4 characters
                                                const rangeYear =
                                                  extractedYear ||
                                                  parseInt(selectedRange.from.substring(0, 4));
                                                selectedYear = rangeYear;
                                                transactionKey = `${customer.customer}-${rangeYear}-${monthNumber}`;
                                              } else {
                                                const latestYear = Math.max(
                                                  ...Array.from(selectedYears)
                                                );
                                                selectedYear =
                                                  selectedTransactionYear[monthKey] || latestYear;
                                                transactionKey = `${customer.customer}-${selectedYear}-${monthNumber}`;
                                              }

                                              const isMonthExpanded = expandedMonths.has(monthKey);
                                              const isLoadingTransactions =
                                                loadingTransactions.has(transactionKey);
                                              const allTransactions =
                                                monthTransactions[transactionKey] || [];

                                              // Filter transactions to only show those within the selected range (for custom ranges)
                                              const transactions =
                                                comparisonMode === 'customRanges' && selectedRange
                                                  ? allTransactions.filter((tx) => {
                                                      const txDate = parseDate(tx.document_date);
                                                      if (!txDate) return false;
                                                      txDate.setHours(0, 0, 0, 0);
                                                      const fromDate = parseDate(
                                                        selectedRange.from
                                                      );
                                                      if (!fromDate) return false;
                                                      fromDate.setHours(0, 0, 0, 0);
                                                      const toDate = parseDate(selectedRange.to);
                                                      if (!toDate) return false;
                                                      toDate.setHours(23, 59, 59, 999);
                                                      return txDate >= fromDate && txDate <= toDate;
                                                    })
                                                  : allTransactions;

                                              // Calculate document count and revenue for custom ranges (if rangeData not available)
                                              let calculatedDocCount = 0;
                                              let calculatedRevenue = 0;
                                              if (
                                                comparisonMode === 'customRanges' &&
                                                selectedRange
                                              ) {
                                                const rangeKey = `${selectedRange.from}_${selectedRange.to}`;
                                                const rangeData = month.rangeData?.[rangeKey];
                                                // Use rangeData if available, otherwise calculate from filtered transactions
                                                calculatedDocCount =
                                                  rangeData?.documentCount ?? transactions.length;
                                                calculatedRevenue =
                                                  rangeData?.invoiceTotal ??
                                                  transactions.reduce(
                                                    (sum, tx) => sum + (tx.net_sales || 0),
                                                    0
                                                  );
                                              }

                                              return (
                                                <React.Fragment key={index}>
                                                  <tr
                                                    className="tr cursor-pointer hover:bg-gray-50"
                                                    onClick={() =>
                                                      toggleMonthExpansion(
                                                        customer.customer,
                                                        monthNumber
                                                      )
                                                    }
                                                  >
                                                    <td className="td font-medium">
                                                      <div className="flex items-center gap-2">
                                                        <div
                                                          className={`chevron ${isMonthExpanded ? 'expanded' : ''}`}
                                                        >
                                                          <svg
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                            className="w-4 h-4"
                                                          >
                                                            <path
                                                              fillRule="evenodd"
                                                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                              clipRule="evenodd"
                                                            />
                                                          </svg>
                                                        </div>
                                                        {month.month}
                                                      </div>
                                                    </td>
                                                    {comparisonMode === 'years' ? (
                                                      <>
                                                        {/* All Doc counts together */}
                                                        {Array.from(selectedYears)
                                                          .sort((a, b) => a - b)
                                                          .map((year) => {
                                                            const yearData = month.yearData?.[year];
                                                            return (
                                                              <td
                                                                key={year}
                                                                className="td text-right"
                                                              >
                                                                <span className="font-medium">
                                                                  {yearData?.documentCount || 0}
                                                                </span>
                                                              </td>
                                                            );
                                                          })}
                                                        {/* All Revenue together */}
                                                        {Array.from(selectedYears)
                                                          .sort((a, b) => a - b)
                                                          .map((year) => {
                                                            const yearData = month.yearData?.[year];
                                                            return (
                                                              <td
                                                                key={year}
                                                                className="td text-right"
                                                              >
                                                                <span className="font-medium">
                                                                  {formatCurrency(
                                                                    yearData?.invoiceTotal || 0
                                                                  )}
                                                                </span>
                                                              </td>
                                                            );
                                                          })}
                                                        {/* Growth columns between consecutive years */}
                                                        {Array.from(selectedYears)
                                                          .sort((a, b) => a - b)
                                                          .slice(1)
                                                          .map((year, index) => {
                                                            const prevYear = Array.from(
                                                              selectedYears
                                                            ).sort((a, b) => a - b)[index];
                                                            const prevYearData = month.yearData?.[
                                                              prevYear
                                                            ] || {
                                                              year: prevYear,
                                                              documentCount: 0,
                                                              invoiceTotal: 0,
                                                            };
                                                            const currentYearData = month
                                                              .yearData?.[year] || {
                                                              year,
                                                              documentCount: 0,
                                                              invoiceTotal: 0,
                                                            };
                                                            const diff =
                                                              currentYearData.invoiceTotal -
                                                              prevYearData.invoiceTotal;
                                                            const growthPercent =
                                                              prevYearData.invoiceTotal > 0
                                                                ? (diff /
                                                                    prevYearData.invoiceTotal) *
                                                                  100
                                                                : 0;
                                                            const isPositive = diff >= 0;
                                                            return (
                                                              <td
                                                                key={`growth-${prevYear}-${year}`}
                                                                className="td text-right"
                                                              >
                                                                <span
                                                                  className={`badge ${isPositive ? 'badge-green' : 'badge-red'}`}
                                                                >
                                                                  {formatCurrency(diff)} (
                                                                  {growthPercent >= 0 ? '+' : ''}
                                                                  {growthPercent.toFixed(1)}%)
                                                                </span>
                                                              </td>
                                                            );
                                                          })}
                                                      </>
                                                    ) : (
                                                      <>
                                                        {/* Show only selected range data */}
                                                        {selectedRange && (
                                                          <>
                                                            <td className="td text-right">
                                                              <span className="font-medium">
                                                                {calculatedDocCount}
                                                              </span>
                                                            </td>
                                                            <td className="td text-right">
                                                              <span className="font-medium">
                                                                {formatCurrency(calculatedRevenue)}
                                                              </span>
                                                            </td>
                                                          </>
                                                        )}
                                                      </>
                                                    )}
                                                  </tr>

                                                  {/* Transaction Details */}
                                                  {isMonthExpanded && (
                                                    <tr className="transaction-details">
                                                      <td
                                                        colSpan={
                                                          comparisonMode === 'years'
                                                            ? 1 +
                                                              selectedYears.size * 2 +
                                                              (selectedYears.size - 1) * 2 +
                                                              1
                                                            : 1 +
                                                              customDateRanges.length * 2 +
                                                              (customDateRanges.length - 1) * 2 +
                                                              1
                                                        }
                                                        className="px-6 py-4"
                                                      >
                                                        <div className="transaction-details-card">
                                                          <div className="flex items-center justify-between mb-3">
                                                            <h5 className="font-medium flex items-center gap-2">
                                                              <svg
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                className="w-5 h-5"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                />
                                                              </svg>
                                                              Individual Transactions for{' '}
                                                              {month.month}
                                                            </h5>

                                                            {/* Year Toggle (only for years mode) */}
                                                            {comparisonMode === 'years' && (
                                                              <div className="flex items-center gap-2">
                                                                <span className="text-sm text-gray-600">
                                                                  Year:
                                                                </span>
                                                                <div className="flex bg-gray-100 rounded-lg p-1">
                                                                  {Array.from(selectedYears)
                                                                    .sort((a, b) => a - b)
                                                                    .map((year) => (
                                                                      <button
                                                                        key={year}
                                                                        onClick={() =>
                                                                          toggleTransactionYear(
                                                                            customer.customer,
                                                                            monthNumber,
                                                                            year
                                                                          )
                                                                        }
                                                                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                                                          selectedYear === year
                                                                            ? 'bg-blue-500 text-white'
                                                                            : 'text-gray-600 hover:bg-gray-200'
                                                                        }`}
                                                                      >
                                                                        {year}
                                                                      </button>
                                                                    ))}
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>

                                                          {isLoadingTransactions ? (
                                                            <div className="text-center py-4">
                                                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
                                                              <span className="text-gray-600 font-medium ml-2">
                                                                Loading transactions...
                                                              </span>
                                                            </div>
                                                          ) : transactions.length > 0 ? (
                                                            <div className="table-scroll max-h-64">
                                                              <table className="table">
                                                                <thead>
                                                                  <tr>
                                                                    <th className="th">
                                                                      Document #
                                                                    </th>
                                                                    <th className="th">Date</th>
                                                                    <th className="th">Type</th>
                                                                    <th className="th text-right">
                                                                      Amount
                                                                    </th>
                                                                  </tr>
                                                                </thead>
                                                                <tbody>
                                                                  {transactions.map(
                                                                    (tx, txIndex) => (
                                                                      <tr
                                                                        key={txIndex}
                                                                        className="tr cursor-pointer hover:bg-gray-50"
                                                                        onClick={(e) => {
                                                                          e.stopPropagation();
                                                                          setSelectedInvoice(tx);
                                                                        }}
                                                                      >
                                                                        <td className="td font-mono text-sm">
                                                                          {tx.document_number}
                                                                        </td>
                                                                        <td className="td">
                                                                          {formatDate(
                                                                            tx.document_date
                                                                          )}
                                                                        </td>
                                                                        <td className="td">
                                                                          <span
                                                                            className={`badge ${tx.document_type.includes('Invoice') ? 'badge-green' : 'badge-red'}`}
                                                                          >
                                                                            {tx.document_type}
                                                                            {tx.transaction_status ===
                                                                            'unposted'
                                                                              ? ' (unposted)'
                                                                              : ''}
                                                                          </span>
                                                                        </td>
                                                                        <td className="td text-right">
                                                                          <span
                                                                            className={`font-medium ${tx.net_sales >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                                                          >
                                                                            {formatCurrency(
                                                                              tx.net_sales
                                                                            )}
                                                                          </span>
                                                                        </td>
                                                                      </tr>
                                                                    )
                                                                  )}
                                                                </tbody>
                                                              </table>
                                                            </div>
                                                          ) : (
                                                            <div className="text-center py-4 text-gray-500">
                                                              {comparisonMode === 'years'
                                                                ? `No transactions found for ${selectedYear}.`
                                                                : `No transactions found within the selected date range.`}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  )}
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {filteredCustomerData.length > itemsPerPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  borderTop: '1px solid #dee2e6',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                    Showing{' '}
                    {Math.min((currentPage - 1) * itemsPerPage + 1, filteredCustomerData.length)} -{' '}
                    {Math.min(currentPage * itemsPerPage, filteredCustomerData.length)} of{' '}
                    {filteredCustomerData.length} customers
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                    }}
                  >
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                    <option value={200}>200 per page</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="btn btn-sm"
                    style={{
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-sm"
                    style={{
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '0.875rem', padding: '0 0.5rem' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="btn btn-sm"
                    style={{
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="btn btn-sm"
                    style={{
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Detail Dialog */}
      {selectedInvoice && (
        <InvoiceDetailDialog
          key={selectedInvoice.document_number}
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
};

export default SalesReportsPage;
